import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import { STAGES } from "../../constants/dealStages";
import { fmt } from "../../utils/formatters";
import { parseDealName } from "../../utils/parseDealName";
import StageBadge from "../common/StageBadge";
import BuyBoxChip from "../common/BuyBoxChip";

export default function DealCard({ deal, onClick, photoUrl, onStageChange, readOnly = false }) {
  const firstAddress = deal.addresses?.[0] ?? "";
  const extraCount   = (deal.addresses?.length ?? 1) - 1;
  const dealName     = parseDealName(deal.sourceNotes);
  const [stageMenu, setStageMenu] = useState(false);
  // Portal-positioned dropdown — escapes the card's `overflow-hidden` (which
  // was clipping the menu) by rendering at the document root in fixed coords.
  const stageBtnRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  function recalcPos() {
    if (!stageBtnRef.current) return;
    const rect = stageBtnRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }

  useLayoutEffect(() => {
    if (stageMenu) recalcPos();
  }, [stageMenu]);

  // Reposition on scroll/resize so the menu stays anchored to its badge.
  // Capture-phase scroll listener catches scrolls in any ancestor, not just window.
  useEffect(() => {
    if (!stageMenu) return;
    const handler = () => recalcPos();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [stageMenu]);

  return (
    <div
      onClick={onClick}
      className="bg-[rgba(44,44,44,0.7)] hover:bg-[#2c2c2c] border border-[rgba(58,58,58,0.5)] hover:border-[rgba(233,53,41,0.4)] rounded-xl transition-all group shadow-sm overflow-hidden cursor-pointer"
    >
      {photoUrl && (
        <div className="w-full" style={{ height: 120 }}>
          <img src={photoUrl} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-white leading-snug group-hover:text-[#f04f44] transition-colors truncate">
                {dealName || firstAddress}
              </p>
              {extraCount > 0 && (
                <span className="flex-shrink-0 text-xs bg-[#3a3a3a] text-[#8a8a8a] rounded px-1.5 py-0.5">
                  +{extraCount} more
                </span>
              )}
            </div>
            {dealName && (
              <p className="text-xs text-[#8a8a8a] truncate mt-0.5">{firstAddress}{extraCount > 0 ? ` +${extraCount} more` : ""}</p>
            )}
          </div>

          {/* CFO date badge + Marketed badge + Stage badge */}
          <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {deal.callForOffersDate && (() => {
              // Color-code by urgency: red <7d, amber <30d, slate otherwise.
              // Date is YYYY-MM-DD; parse as UTC so we don't shift by tz.
              const [y, m, d] = String(deal.callForOffersDate).split("-").map(Number);
              const cfoMs = Date.UTC(y, m - 1, d);
              const todayMs = Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());
              const daysLeft = Math.round((cfoMs - todayMs) / 86_400_000);
              const past = daysLeft < 0;
              const urgent = !past && daysLeft <= 7;
              const soon   = !past && daysLeft <= 30 && !urgent;
              const palette = past
                ? { bg: "rgba(138,138,138,0.15)", fg: "#8a8a8a", bd: "rgba(138,138,138,0.3)" }
                : urgent
                ? { bg: "rgba(233,53,41,0.15)",  fg: "#e93529", bd: "rgba(233,53,41,0.3)" }
                : soon
                ? { bg: "rgba(200,146,47,0.15)", fg: "#c8922f", bd: "rgba(200,146,47,0.3)" }
                : { bg: "rgba(138,138,138,0.12)",fg: "#c4c4c4", bd: "rgba(138,138,138,0.25)" };
              const label = `CFO ${m}/${d}`;
              const title = past
                ? `Call For Offers was ${Math.abs(daysLeft)}d ago`
                : daysLeft === 0
                ? "Call For Offers today"
                : `Call For Offers in ${daysLeft}d`;
              return (
                <span title={title} className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}` }}>
                  {label}
                </span>
              );
            })()}
            {deal.marketed && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(233,53,41,0.1)", color: "#e93529", border: "1px solid rgba(233,53,41,0.2)" }}>
                MKT
              </span>
            )}
            <BuyBoxChip
              verdict={deal.buyBoxVerdict}
              source={deal.buyBoxSource}
              reason={deal.buyBoxReason}
              size="sm"
            />
            {readOnly ? (
              <span title={deal.stage}>
                <StageBadge stage={deal.stage} />
              </span>
            ) : (
              <button
                ref={stageBtnRef}
                onClick={() => setStageMenu((v) => !v)}
                className="flex items-center gap-0.5 hover:opacity-80 transition-opacity"
                title="Change stage"
              >
                <StageBadge stage={deal.stage} />
                <span className="text-[#8a8a8a] text-xs leading-none">▾</span>
              </button>
            )}
            {!readOnly && stageMenu && createPortal(
              // This wrapper used to carry .theme-light so index.css could
              // repaint the menu once it left the app root. There is no theme
              // layer any more - the menu below states its own brand colours -
              // so the wrapper is just a fragment holding the two layers.
              <>
                <div className="fixed inset-0 z-[60]" onClick={() => setStageMenu(false)} />
                <div
                  className="fixed z-[61] bg-[#2c2c2c] border border-[#3a3a3a] rounded-lg shadow-xl py-1 w-44 max-h-[60vh] overflow-y-auto"
                  style={{ top: menuPos.top, right: menuPos.right }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {STAGES.map((s) => (
                    <button
                      key={s}
                      onClick={() => { onStageChange?.(deal.id, s); setStageMenu(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-[#3a3a3a] transition-colors ${
                        s === deal.stage ? "text-[#f04f44] font-semibold" : "text-[#b4b4b4]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </>,
              document.body
            )}
          </div>
        </div>

        <div className="flex items-baseline justify-between gap-2 mb-4">
          <p className="text-xs text-[#8a8a8a] truncate">{deal.market}</p>
          {deal.createdAt && (() => {
            const added = new Date(deal.createdAt);
            const days = Math.max(0, Math.floor((Date.now() - added.getTime()) / 86_400_000));
            const ageStr = days === 0 ? "today" : days === 1 ? "1d ago" : `${days}d ago`;
            const tooltip = `Added ${added.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
            return (
              <span title={tooltip} className="text-[10px] text-[#8a8a8a] flex-shrink-0">Added {ageStr}</span>
            );
          })()}
        </div>

        {/* Financial metrics row — only render the blocks (and the row itself)
            for fields that actually have a value. Avoids the wall of "TBD". */}
        {(() => {
          const hasSize = deal.squareFeet > 0;
          const hasAsk  = deal.askingPrice > 0;
          const hasCap  = deal.capRate > 0;
          if (!hasSize && !hasAsk && !hasCap) return null;
          return (
            <div className="flex items-start justify-between gap-6">
              {hasSize && (
                <div>
                  <p className="text-xs text-[#8a8a8a] mb-0.5">Size</p>
                  <p className="text-sm font-semibold text-[#f4f4f4] whitespace-nowrap">{fmt.sf(deal.squareFeet)}</p>
                </div>
              )}
              {hasAsk && (
                <div>
                  <p className="text-xs text-[#8a8a8a] mb-0.5">Ask</p>
                  <p className="text-sm font-semibold text-[#f4f4f4] whitespace-nowrap">
                    {deal.askingPrice >= 1_000_000
                      ? `$${(deal.askingPrice / 1_000_000).toFixed(1)}M`
                      : `$${Number(deal.askingPrice).toLocaleString()}`}
                    {hasSize && ` ($${Math.round(deal.askingPrice / deal.squareFeet)}/SF)`}
                  </p>
                </div>
              )}
              {hasCap && (
                <div>
                  {/* "Going-In Cap", not "Cap Rate". deal.capRate is the cap on
                      year-one NOI at our price; the card also carries an exit
                      cap elsewhere, and two things both labelled "cap rate" are
                      one glance away from being read as the same number. */}
                  <p className="text-xs text-[#8a8a8a] mb-0.5">Going-In Cap</p>
                  <p className="text-sm font-semibold text-[#f04f44] whitespace-nowrap">{fmt.cap(deal.capRate)}</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* UW metrics row — only shown if at least one value exists */}
        {(deal.underwrittenPrice || deal.leveredIrr || deal.equityMultiple || deal.trendedYoc || deal.untrendedYoc || deal.averageCoc) && (
          <div className="mt-2 pt-2 border-t border-[rgba(58,58,58,0.3)] flex flex-wrap gap-x-3 gap-y-0.5">
            {deal.underwrittenPrice && (
              <span className="text-xs text-[#8a8a8a]">PPUSA: <span className="text-[#f04f44] font-medium">
                {`${
                  deal.underwrittenPrice >= 1_000_000
                    ? `$${(deal.underwrittenPrice / 1_000_000).toFixed(1)}M`
                    : `$${Number(deal.underwrittenPrice).toLocaleString()}`
                }${
                  deal.squareFeet > 0
                    ? ` ($${Math.round(deal.underwrittenPrice / deal.squareFeet)}/SF)`
                    : ""
                }`}
              </span></span>
            )}
            {deal.leveredIrr && (
              <span className="text-xs text-[#8a8a8a]">IRR: <span className="text-[#f4f4f4] font-medium">{fmt.pct(deal.leveredIrr)}</span></span>
            )}
            {deal.equityMultiple && (
              <span className="text-xs text-[#8a8a8a]">EM: <span className="text-[#f4f4f4] font-medium">{fmt.multiple(deal.equityMultiple)}</span></span>
            )}
            {deal.trendedYoc && (
              <span className="text-xs text-[#8a8a8a]">T-YoC: <span className="text-[#f4f4f4] font-medium">{fmt.pct(deal.trendedYoc)}</span></span>
            )}
            {deal.untrendedYoc && (
              <span className="text-xs text-[#8a8a8a]">U-YoC: <span className="text-[#f4f4f4] font-medium">{fmt.pct(deal.untrendedYoc)}</span></span>
            )}
            {deal.averageCoc && (
              <span className="text-xs text-[#8a8a8a]">CoC: <span className="text-[#f4f4f4] font-medium">{fmt.pct(deal.averageCoc)}</span></span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
